import { inject, Injectable, OnDestroy } from '@angular/core';
import {
    catchError,
    combineLatest,
    concat,
    filter,
    finalize,
    map,
    merge,
    Observable,
    of,
    scan,
    share,
    shareReplay,
    startWith,
    Subject,
    switchMap,
    take,
    takeLast,
    takeUntil,
    tap,
    withLatestFrom
} from 'rxjs';
import { injectAiChatEventNotifier } from './ai-chat-events.provider';
import { AiChatThreadService } from './ai-chat-thread.service';
import {
    AiChatApiService,
    AiChatInternalPromptService,
    PromptResponse,
    ResponseCommand,
    RestoreThreadResponse,
    UserInteractCommand
} from '@panaya/ai-chat/domain';
import { busySignal, silenceSignal } from '@panaya/ai-chat/utils';
import {
    ActionSuggestion,
    AssistantMessage,
    ChatMessage,
    InternalPrompt,
    Prompt,
    UserFeedback,
    UserFeedbackRequest,
    UserMessage,
    UserPrompt
} from '@panaya/ai-chat/shared';
import { AiChatActionsSuggestionsProviderService } from './action-suggestions';
import { AiChatThreadIdProviderService, extractThreadIdFromResponse } from './ai-chat-thread-id-provider.service';
import { AiChatFinishTypingService } from './ai-chat-finish-typing.service';
import { typer } from '@panaya/utils';

const ASSISTANT_SILENCE_MS = 700;

/**
 * Represents the state of a conversation with the AI server.
 * This service connects the active thread with ui events to create a reactive
 * conversation API, allowing different features in the widget to subscribe
 * to messages and reflect them in whichever way required.
 */
@Injectable({
    providedIn: 'root'
})
export class AiChatConversationService implements OnDestroy {
    private readonly destroy$ = new Subject<void>();
    private readonly uiEvents = injectAiChatEventNotifier();
    private readonly internalPromptService = inject(AiChatInternalPromptService);
    private readonly ai = inject(AiChatApiService);
    private readonly threadService = inject(AiChatThreadService);
    private readonly actionsSuggestionsProvider = inject(AiChatActionsSuggestionsProviderService);
    private readonly threadIdProvider = inject(AiChatThreadIdProviderService);
    private readonly finishTypingService = inject(AiChatFinishTypingService);

    // Internal service streams used as base for other streams
    private readonly prompt = this.createPromptFeed();
    private readonly initialMessages = this.createInitialMessagesFeed();

    // Public streams used to expose conversation state and events
    /**
     * Emits messages (prompts) received from the user in a unified format for chat messages.
     */
    public readonly userMessage = this.createUserMessageFeed();
    /**
     * Emits messages (prompt responses) received from the AI assistant in a unified format for chat messages.
     */
    public readonly assistantMessage = this.createAssistantMessageFeed();
    /**
     * Holds the conversation state of the current thread and emits arrays representing
     * the full chat log so far for the current thread in a unified format for chat messages.
     *
     * If a new thread is initialized, this will first emit an empty array.
     * If a thread is restored, this will first emit an array with the restored chat log.
     */
    public readonly messages = this.createMessagesFeed();
    /**
     * Emits a boolean value indicating whether the AI assistant has started working.
     * `true` if the assistant is processing a prompt.
     * `false` if the assistant has returned the first response.
     */
    public readonly assistantThinking = busySignal(
        merge(
            this.uiEvents.promptRequest,
            this.internalPromptService.internalPrompt$.pipe(filter(({ showLoading }: InternalPrompt) => !!showLoading))
        ),
        this.assistantMessage
    );
    /**
     * Emits a boolean value indicating whether the AI assistant is still working.
     * `true` if the assistant is processing a prompt.
     * `false` if the assistant is idle.
     */
    private readonly finishTyping = new Subject<void>();
    public readonly assistantWorking = busySignal(
        merge(
            this.uiEvents.promptRequest,
            this.internalPromptService.internalPrompt$.pipe(filter(({ showLoading }: InternalPrompt) => !!showLoading))
        ),
        this.finishTyping
    );
    /**
     * Emits `true` when the AI assistant is still working but no text has been typed for a while,
     * e.g. during a long tool call after a preamble message. Used to bring back the thinking indicator.
     */
    public readonly assistantSilent = silenceSignal(this.assistantWorking, this.assistantMessage, ASSISTANT_SILENCE_MS);
    public readonly sourceLink = this.ai.sourceLink;

    constructor() {
        // Listen to interrupt events from UI and trigger interrupt.
        // This is a manual subscription because:
        // 1. It's a pure side-effect (no output data)
        // 2. It bridges UI events to API calls
        // 3. The actual interrupt mechanism is reactive (via takeUntil in ai.prompt())
        this.uiEvents.interruptRequest.pipe(takeUntil(this.destroy$)).subscribe(() => this.ai.interrupt());
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private createPromptFeed(): Observable<readonly [prompt: Prompt, threadId: string | null]> {
        return merge(this.uiEvents.promptRequest, this.internalPromptService.internalPrompt$).pipe(
            withLatestFrom(this.threadService.id),
            map(([prompt, threadId]) => [prompt, threadId?.value ?? null] as const)
        );
    }

    private createUserMessageFeed(): Observable<UserMessage> {
        return this.prompt.pipe(
            map(([prompt]) => prompt),
            filter(isUserPrompt),
            map(userPromptToMessageFormat)
        );
    }

    private createAssistantMessageFeed(): Observable<AssistantMessage> {
        return this.prompt.pipe(
            switchMap(([prompt, threadId]) => {
                const prompt$ = this.ai.prompt(threadId, prompt.message, prompt.contextExtras).pipe(
                    catchError(() => of(getPromptErrorMessage())),
                    tap((response) => {
                        // Extract and notify session_id from prompt response if present
                        const extractedThreadId = extractThreadIdFromResponse(response);
                        if (extractedThreadId) {
                            this.threadIdProvider.notifyThreadId(extractedThreadId);
                        }
                    }),
                    shareReplay(1)
                );

                const userInteractCommand$ = prompt$.pipe(map(getUserInteractCommand));

                // Share the typer so both phases can reference the same typed characters.
                // takeUntil ensures it completes promptly on interrupt (required for takeLast in phase 2).
                const typedMessage$ = typer(
                    userInteractCommand$.pipe(map((cmd) => cmd?.message ?? ''))
                ).pipe(
                    takeUntil(this.uiEvents.interruptRequest),
                    shareReplay(1)
                );

                // Phase 1: every typed character flows through immediately with undefined suggestions.
                // finalize fires when typing ends (naturally or interrupted), signalling completion.
                const typingMessages$ = typedMessage$.pipe(
                    withLatestFrom(userInteractCommand$),
                    map(([msg, cmd]) => [msg, cmd, undefined as ActionSuggestion[] | undefined] as const),
                    finalize(() => {
                        this.finishTypingService.notifyFinishTyping();
                        this.finishTyping.next();
                    })
                );

                // Phase 2: one final emission after typing, carrying the actual action suggestions.
                // takeLast(1) safely handles the case where typing was interrupted before any character.
                const finalWithSuggestions$ = combineLatest([
                    typedMessage$.pipe(takeLast(1)),
                    userInteractCommand$,
                    this.actionsSuggestionsProvider.actionsSuggestions$.pipe(take(1))
                ]).pipe(
                    map(([msg, cmd, suggestions]) => [msg, cmd, suggestions] as const)
                );

                return concat(typingMessages$, finalWithSuggestions$);
            }),
            map(([message, userInteractCommand, actionsSuggestions]) =>
                promptResponseToMessageFormat(message, userInteractCommand?.messageId ?? '', actionsSuggestions)
            ),
            share()
        );
    }

    private createInitialMessagesFeed(): Observable<ChatMessage[]> {
        // If thread has just initialized or has closed, reset the initial messages array
        const resetConversation = this.threadService.id.pipe(
            filter(({ source }) => source === 'init' || source === 'close'),
            map(() => [])
        );
        // If thread should be restored, call the api and set the new array
        const restoreConversation = this.threadService.id.pipe(
            filter((data): data is { value: string; source: 'restore' } => data.source === 'restore'),
            switchMap(({ value: threadId }) => this.ai.restoreThread(threadId)),
            map(chatHistoryToMessageFormat)
        );

        return merge(resetConversation, restoreConversation).pipe(shareReplay(1));
    }

    private createMessagesFeed(): Observable<ChatMessage[]> {
        const userMessageUpdater$ = this.userMessage.pipe(map((message) => (messages: ChatMessage[]) => [...messages, message]));
        const assistantMessageUpdater$ = this.assistantMessage.pipe(map(this.createAssistantMessageUpdater));
        const userFeedbackUpdater$ = this.sendUserFeedback$().pipe(map(this.createUserFeedbackUpdater));

        const messagesUpdaters$ = merge(userMessageUpdater$, assistantMessageUpdater$, userFeedbackUpdater$);

        return this.initialMessages.pipe(
            switchMap((initial) =>
                messagesUpdaters$.pipe(
                    scan((messages, applyMessagesUpdater) => applyMessagesUpdater(messages), [...initial]),
                    startWith(initial)
                )
            ),
            shareReplay(1)
        );
    }

    private sendUserFeedback$(): Observable<UserFeedbackRequest> {
        return this.uiEvents.userFeedbackRequest.pipe(
            withLatestFrom(this.threadService.id),
            switchMap(([{ messageId, userFeedback }, threadId]) =>
                this.ai.sendFeedback(threadId.value ?? '', messageId, userFeedback).pipe(
                    map(() => ({
                        messageId,
                        userFeedback
                    }))
                )
            )
        );
    }

    private createAssistantMessageUpdater(message: ChatMessage) {
        return (messages: ChatMessage[]): ChatMessage[] => mergeIntoChatLog(messages, message);
    }

    private createUserFeedbackUpdater({ messageId, userFeedback }: UserFeedbackRequest) {
        return (messages: ChatMessage[]): ChatMessage[] => updateMessageWithUserFeedback(messages, messageId, userFeedback);
    }
}

function isUserPrompt(prompt: Prompt): prompt is UserPrompt {
    return prompt.source === 'user' || !prompt.hidden;
}

function userPromptToMessageFormat({ message, context, file, attachments }: UserPrompt): UserMessage {
    const markdownContent = file ? '' : message;

    return { source: 'user' as const, markdownContent, extras: { context, file, attachments } };
}

function promptResponseToMessageFormat(
    markdownContent: string,
    messageId: string,
    actionsSuggestions: ActionSuggestion[] | undefined
): AssistantMessage {
    return { source: 'assistant' as const, markdownContent, extras: { links: [], actionsSuggestions, messageId } };
}

function chatHistoryToMessageFormat({ chat_history }: RestoreThreadResponse): ChatMessage[] {
    return chat_history.reduce<ChatMessage[]>(
        (messages, { role: source, content: markdownContent }) => [...messages, { source, markdownContent }],
        []
    );
}

function mergeIntoChatLog(chatLog: ChatMessage[], newMessage: ChatMessage): ChatMessage[] {
    const lastMessage = chatLog[chatLog.length - 1];
    if (newMessage.source === 'assistant' && lastMessage?.source === 'assistant') {
        return mergeIntoLastMessage(chatLog, newMessage);
    }

    return [...chatLog, newMessage];
}

function mergeIntoLastMessage(chatLog: ChatMessage[], newMessage: ChatMessage): ChatMessage[] {
    return [...chatLog.slice(0, -1), newMessage];
}

function updateMessageWithUserFeedback(messages: ChatMessage[], messageId: string, userFeedback: UserFeedback): ChatMessage[] {
    return messages.map((message) =>
        message.source === 'assistant' && message.extras?.messageId === messageId
            ? { ...message, extras: { ...message.extras, feedbackMode: userFeedback.mode } }
            : message
    );
}

function getUserInteractCommand(promptResponse: PromptResponse): UserInteractCommand | undefined {
    return promptResponse.commands?.find(isUserInteractCommand);
}

function isUserInteractCommand(command: ResponseCommand): command is UserInteractCommand {
    return command.type === 'user_interact';
}

function getPromptErrorMessage(): PromptResponse {
    return {
        commands: [
            {
                type: 'user_interact',
                message: `Sorry. Seems like I need to recover from something...\nCould you try me later?`,
                messageId: ''
            }
        ]
    };
}
